import { useEventBus } from '@/composables/useEventBus';
import type { OnboardingStepDeps } from '@/composables/useOnboarding';
import i18n from '@/i18n';
import { createActionStep, createInfoStep } from '@/onboarding/stepStyles';
import { useDishesStore } from '@/stores/dishesStore';
import type { StepOptions } from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { nextTick } from 'vue';
import type { Composer } from 'vue-i18n';

const t = (key: string, params: Record<string, unknown> = {}) => (i18n.global as unknown as Composer).t(key, params);

const eventBus = useEventBus();

export function getRecipeGenerationSteps({ tour, router, waitForElement }: OnboardingStepDeps): StepOptions[] {
  const dishesStore = useDishesStore();

  return [
    // 5 — Création de recette (action requise)
    createActionStep({
      id: 'create-recipe',
      title: t('onboarding.chapters.introduction.steps.createRecipe.title'),
      text: t('onboarding.chapters.introduction.steps.createRecipe.text'),
      attachTo: { element: '#generate-recipes-button', on: 'bottom' },
      canClickTarget: true,
      advanceOn: { selector: '#generate-recipes-button', event: 'click' },
      beforeShowPromise: async () => {
        if (router.currentRoute.value.path !== '/') {
          console.log("[Onboarding] Retour à l'accueil...");
          await router.push({ path: '/' });
        }
        await waitForElement('#generate-recipes-button', 10000);
        await nextTick();
      },
    }),
    createInfoStep({
      id: 'tab-enonce',
      title: t('onboarding.chapters.recipeGeneration.steps.tab-enonce.title'),
      text: t('onboarding.chapters.recipeGeneration.steps.tab-enonce.text'),
      canClickTarget: false,
      attachTo: { element: '#menu-title-textarea', on: 'bottom' },
      beforeShowPromise: async () => {
        console.log('Navigating to /menu/create with type=from_title');
        dishesStore.generationParams.generation_type = 'from_title';
        await router.push({ path: '/menu/create', query: { type: 'from_title' } });
        await waitForElement('#menu-title-textarea', 10000);
        await nextTick();
        console.log('Updated generationParams:', dishesStore.generationParams);
      },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        {
          text: t('onboarding.common.next'),
          action: () => {
            tour.value?.next();
          },
        },
      ],
    }),
    createActionStep(
      {
        id: 'action-goto-produits',
        title: t('onboarding.chapters.recipeGeneration.steps.action-goto-produits.title'),
        text: t('onboarding.chapters.recipeGeneration.steps.action-goto-produits.text', {
          dynamicText: `<strong>${t('onboarding.bold.chapter_2.tab_products')}</strong>`,
        }),
        attachTo: { element: '#tab-button-produits', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '#tab-button-produits', event: 'click' },
        beforeShowPromise: async () => {
          await waitForElement('#tab-button-produits', 10000);
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    createInfoStep({
      id: 'tab-produits',
      title: t('onboarding.chapters.recipeGeneration.steps.tab-produits.title'),
      text: t('onboarding.chapters.recipeGeneration.steps.tab-produits.text'),
      canClickTarget: false,
      attachTo: { element: '#ingredient-textarea', on: 'bottom' },
      beforeShowPromise: async () => {
        console.log('[Onboarding] Changement d\'état vers "with_main_product"');
        dishesStore.generationParams.generation_type = 'with_main_product';

        await nextTick();

        console.log('Waiting for DOM to stabilize for tab-produits');
        await waitForElement('#ingredient-textarea', 10000);
        await nextTick();
      },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        {
          text: t('onboarding.common.next'),
          action: () => {
            tour.value?.next();
          },
        },
      ],
    }),
    createActionStep(
      {
        id: 'action-goto-quick',
        title: t('onboarding.chapters.recipeGeneration.steps.action-goto-quick.title'),
        text: t('onboarding.chapters.recipeGeneration.steps.action-goto-quick.text', {
          dynamicText: `<strong>${t('onboarding.bold.chapter_2.tab_fast')}</strong>`,
        }),
        attachTo: { element: '#tab-button-quick', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '#tab-button-quick', event: 'click' },
        beforeShowPromise: async () => {
          await waitForElement('#tab-button-quick', 10000);
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    createInfoStep({
      id: 'quick-creation-category',
      title: t('onboarding.chapters.recipeGeneration.steps.quick-creation-category.title'),
      text: t('onboarding.chapters.recipeGeneration.steps.quick-creation-category.text'),
      canClickTarget: true,
      attachTo: { element: '#menuDishType', on: 'top' },

      beforeShowPromise: async () => {
        console.log('[Onboarding] Changement d\'état vers "quick"');
        dishesStore.generationParams.generation_type = 'quick';

        await nextTick();

        console.log('Waiting for DOM to stabilize for quick-creation');
        await waitForElement('#menuDishType', 10000);
        await nextTick();
      },

      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        {
          text: t('onboarding.common.next'),
          action: () => {
            const sidebar = document.querySelector('.ingredient-detail-panel');
            const closeButton = document.querySelector('.ingredient-detail-panel .onboarding-ingredient-detail-button');
            if (closeButton) {
              (closeButton as HTMLElement).click();
            } else if (sidebar) {
              (sidebar as HTMLElement).style.display = 'none';
            }
            tour.value?.next();
          },
        },
      ],
    }),
    createActionStep(
      {
        id: 'quick-creation-generate',
        title: t('onboarding.chapters.recipeGeneration.steps.quick-creation-generate.title'),
        text: t('onboarding.chapters.recipeGeneration.steps.quick-creation-generate.text'),
        canClickTarget: true,
        attachTo: { element: '#createButton', on: 'top' },
        advanceOn: { selector: '#createButton', event: 'click' },
        beforeShowPromise: async () => {
          if (router.currentRoute.value.path !== '/menu/create') {
            console.log('[Onboarding] Retour à /menu/create...');
            await router.push({ path: '/menu/create' });
          }

          if (dishesStore.generationParams.generation_type !== 'quick') {
            console.log('[Onboarding] Changement d\'état vers "quick"');
            dishesStore.generationParams.generation_type = 'quick';
            await nextTick();
          }

          console.log('Waiting for DOM to stabilize for quick-creation-generate');
          await waitForElement('#createButton', 10000);
          await nextTick();
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'bottom',
      },
    ),
    // createWaitingStep({
    //   id: 'wait-generation',
    //   title: '⏳🍽️ Génération des recettes en cours',
    //   text: 'Patientez pendant la génération des suggestions de recettes.',
    //   attachTo: { element: '#dishesListResult', on: 'bottom' },
    //   canClickTarget: false,
    //   extraHighlights: ['.progressBar'],
    //   beforeShowPromise: async () => {
    //     try {
    //       await waitForElement('#dishesListResult', 15000);
    //       await nextTick();
    //     } catch (error) {
    //       console.error('[Onboarding] Error waiting for dishesListResult:', error);
    //     }
    //   },
    //   buttons: [],
    // }),

    createActionStep(
      {
        id: 'select-recipe',
        title: t('onboarding.chapters.recipeGeneration.steps.select-recipe.title'),
        text: t('onboarding.chapters.recipeGeneration.steps.select-recipe.text'),
        canClickTarget: true,
        attachTo: { element: '#dishesListResult', on: 'bottom' },
        beforeShowPromise: async () => {
          await waitForElement('#dishesListResult', 10000);
          await waitForElement('.recipe-item', 10000);
          console.log('[Onboarding] Étape 6 - Éléments présents:', {
            dishesListResult: !!document.querySelector('#dishesListResult'),
            recipeItems: document.querySelectorAll('.recipe-item').length,
          });
          await nextTick();
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    createActionStep(
      {
        id: 'validate-recipe-selection',
        title: t('onboarding.chapters.recipeGeneration.steps.validate-recipe-selection.title'),
        text: t('onboarding.chapters.recipeGeneration.steps.validate-recipe-selection.text'),
        canClickTarget: true,
        attachTo: { element: '#confirm-recipe-selection-dialog', on: 'top' },
        advanceOn: { selector: '#confirm-recipe-btn', event: 'click' },
        beforeShowPromise: async () => {
          await waitForElement('#confirm-recipe-selection-dialog', 10000);
          await nextTick();
        },
        buttons: [
          {
            text: t('onboarding.common.previous'),
            action: () => {
              eventBus.emit('onboarding:dialog-close-dishes-validation');
              tour.value?.show('select-recipe');
            },
            secondary: true,
          },
        ],
      },
      {
        placement: 'bottom',
      },
    ),
    // createWaitingStep({
    //   id: 'recipe-detail-loading',
    //   title: '⏳ Chargement du détail',
    //   text: 'Génération du détail complet de la recette en cours...',
    //   attachTo: { element: '.generation-result-card', on: 'bottom' },
    //   beforeShowPromise: async () => {
    //     console.log("[Onboarding] Entrant dans l'étape 7 - Chargement du détail");
    //     await waitForElement('.generation-result-card', 10000);
    //     console.log('[Onboarding] .generation-result-card trouvée pour étape 7');
    //     await nextTick();
    //   },
    // }),
    createActionStep(
      {
        id: 'recipe-summary',
        title: t('onboarding.chapters.recipeGeneration.steps.recipe-summary.title'),
        text: t('onboarding.chapters.recipeGeneration.steps.recipe-summary.text'),
        attachTo: { element: '.onboarding-recipe-summary', on: 'bottom' },
        canClickTarget: true,
        beforeShowPromise: async () => {
          if (router.currentRoute.value.path.includes('/menu/dishes/')) {
            console.log('[Onboarding] Retour à la page des résultats...');
            router.back();
          }
          await waitForElement('.onboarding-recipe-summary', 10000);
          await nextTick();
        },
        advanceOn: {
          selector: '.onboarding-recipe-summary',
          event: 'click',
        },
      },
      {
        placement: 'top',
      },
    ),
  ];
}
